import React from 'react';
import RoundedModal from '@src/components/roundedModal/roundedModal';
import { useTranslation } from '@hooks/index';
import StyledButton from '@src/components/form-control/styledButton/styledButton';
import Space from 'antd/lib/space';

type ExportConfirmModalProps = {
  visible: boolean;
  onConfirm: (includeValues: boolean) => void;
  onCancel: () => void;
};

/**
 * Modal to confirm export options - whether to include values
 * @param visible - Whether the modal is visible
 * @param onConfirm - Callback with includeValues boolean
 * @param onCancel - Callback when modal is cancelled
 */
export const ExportConfirmModal = (props: ExportConfirmModalProps) => {
  const { visible, onConfirm, onCancel } = props;
  const t = useTranslation('GlobalVariablesPanel');
  const cw = t.withBaseKey('CommonWords');

  const handleYes = () => {
    onConfirm(true);
  };

  const handleNo = () => {
    onConfirm(false);
  };

  return (
    <RoundedModal
      visible={visible}
      onCancel={onCancel}
      title={t('ExportConfiguration')}
      noFooter={false}
      cancelBtnLabel={cw('Cancel')}
      hideOkBtn={true}
      customFooter={
        <Space size={20}>
          <StyledButton
            color="light"
            title={cw('No')}
            onClick={handleNo}
          />
          <StyledButton
            color="primary"
            title={cw('Yes')}
            onClick={handleYes}
          />
        </Space>
      }
    >
      <div style={{ padding: '20px 0' }}>
        {t('ExportIncludeValues')}
      </div>
    </RoundedModal>
  );
};

type ImportConfirmModalProps = {
  visible: boolean;
  onConfirm: (mode: 'add' | 'replace') => void;
  onCancel: () => void;
};

/**
 * Modal to confirm import mode - add to existing or replace
 * @param visible - Whether the modal is visible
 * @param onConfirm - Callback with mode ('add' or 'replace')
 * @param onCancel - Callback when modal is cancelled
 */
export const ImportConfirmModal = (props: ImportConfirmModalProps) => {
  const { visible, onConfirm, onCancel } = props;
  const t = useTranslation('GlobalVariablesPanel');
  const cw = t.withBaseKey('CommonWords');

  const handleAdd = () => {
    onConfirm('add');
  };

  const handleReplace = () => {
    onConfirm('replace');
  };

  return (
    <RoundedModal
      visible={visible}
      onCancel={onCancel}
      title={t('ImportConfiguration')}
      noFooter={false}
      cancelBtnLabel={cw('Cancel')}
      hideOkBtn={true}
      customFooter={
        <Space size={20}>
          <StyledButton
            color="light"
            title={t('ImportAddToExisting')}
            onClick={handleAdd}
          />
          <StyledButton
            color="primary"
            title={t('ImportReplaceExisting')}
            onClick={handleReplace}
          />
        </Space>
      }
    >
      <div style={{ padding: '20px 0' }}>
        {t('ImportModePrompt')}
      </div>
    </RoundedModal>
  );
};

